import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Volume2, Upload, ChevronUp, ChevronDown, Image, X } from "lucide-react";
import { Button } from "../ui/button";
import apiClient from "../../services/apiClient";
import { CreateModuleModal, EditModuleModal } from "./ModuleModals";

export const ModuleManagement = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [dashboardText, setDashboardText] = useState('');
  const [dashboardImage, setDashboardImage] = useState('');
  const [savingDashboardText, setSavingDashboardText] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchModules();
    fetchDashboardText();
    fetchDashboardImage();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await apiClient.get('/admin/modules');
      setModules(response.data);
    } catch (error) {
      toast.error('Failed to fetch modules');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardText = async () => {
    try {
      const response = await apiClient.get('/settings/dashboard-text');
      setDashboardText(response.data.text || '');
    } catch (error) {
      console.error('Failed to fetch dashboard text:', error);
    }
  };

  const fetchDashboardImage = async () => {
    try {
      const response = await apiClient.get('/settings/dashboard-image');
      setDashboardImage(response.data.filename || '');
    } catch (error) {
      console.error('Failed to fetch dashboard image:', error);
    }
  };

  const handleSaveDashboardText = async () => {
    setSavingDashboardText(true);
    try {
      await apiClient.put('/admin/settings/dashboard-text', { text: dashboardText });
      toast.success('Dashboard-Text gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingDashboardText(false);
    }
  };

  const handleUploadDashboardImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await apiClient.post('/admin/settings/dashboard-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDashboardImage(response.data.filename);
      toast.success('Bild hochgeladen');
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteDashboardImage = async () => {
    if (!window.confirm('Dashboard-Bild wirklich löschen?')) return;
    
    try {
      await apiClient.delete('/admin/settings/dashboard-image');
      setDashboardImage('');
      toast.success('Bild gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Are you sure you want to delete this module?')) return;
    try {
      await apiClient.delete(`/admin/modules/${moduleId}`);
      toast.success('Module deleted successfully');
      fetchModules();
    } catch (error) {
      toast.error('Failed to delete module');
    }
  };

  const handleMoveModule = async (index, direction) => {
    const newModules = [...modules];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= modules.length) return;
    
    [newModules[index], newModules[targetIndex]] = [newModules[targetIndex], newModules[index]];
    
    const moduleOrders = newModules.map((mod, idx) => ({ id: mod.id, order: idx }));
    
    try {
      await apiClient.put('/admin/modules/reorder', { modules: moduleOrders });
      setModules(newModules);
      toast.success('Reihenfolge aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Reihenfolge');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Dashboard Text Editor */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-charcoal-800 mb-4">Dashboard-Text für User</h3>
        <p className="text-sm text-charcoal-600 mb-4">Dieser Text wird im User-Dashboard unter dem Logo angezeigt.</p>
        <textarea
          value={dashboardText}
          onChange={(e) => setDashboardText(e.target.value)}
          className="input-field h-32 resize-none mb-4"
          placeholder="Text eingeben, der für User angezeigt werden soll..."
        />
        <Button onClick={handleSaveDashboardText} disabled={savingDashboardText} className="bg-sage-500 hover:bg-sage-600 text-white">
          {savingDashboardText ? 'Speichern...' : 'Text speichern'}
        </Button>
        
        {/* Dashboard Image Upload */}
        <div className="mt-6 pt-6 border-t border-sage-200">
          <h4 className="text-md font-semibold text-charcoal-800 mb-2">Dashboard-Bild (optional)</h4>
          <p className="text-sm text-charcoal-600 mb-4">Wird neben dem Text auf Desktop und unter dem Text auf Mobile angezeigt.</p>
          
          <div className="flex items-start space-x-4">
            <div className="flex-1">
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleUploadDashboardImage}
                className="input-field"
                disabled={uploadingImage}
              />
              {uploadingImage && <span className="text-sm text-charcoal-500 mt-2 block">Hochladen...</span>}
            </div>
            
            {dashboardImage && (
              <div className="relative">
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/images/${dashboardImage}`}
                  alt="Dashboard Preview"
                  className="w-32 h-32 object-cover rounded-lg border border-sage-200"
                />
                <button
                  onClick={handleDeleteDashboardImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  title="Bild löschen"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Module Management Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-charcoal-800 font-display">Content Management</h2>
        <Button onClick={() => setShowCreateModal(true)} className="bg-sage-500 hover:bg-sage-600 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Add Module
        </Button>
      </div>

      <div className="grid gap-6">
        {modules.map((module, index) => (
          <div key={module.id} className="card p-6">
            <div className="flex justify-between items-start mb-4">
              {/* Move Buttons */}
              <div className="flex flex-col space-y-1 mr-4">
                <Button size="sm" variant="outline" onClick={() => handleMoveModule(index, 'up')} disabled={index === 0}
                  className="text-charcoal-600 border-charcoal-200 hover:bg-charcoal-50 p-1 disabled:opacity-30" title="Nach oben">
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleMoveModule(index, 'down')} disabled={index === modules.length - 1}
                  className="text-charcoal-600 border-charcoal-200 hover:bg-charcoal-50 p-1 disabled:opacity-30" title="Nach unten">
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-charcoal-800 mb-2">{module.title}</h3>
                <p className="text-charcoal-600 mb-2">{module.description}</p>
                <div className="flex items-center flex-wrap gap-2 text-sm text-charcoal-500">
                  <span className="bg-charcoal-100 px-2 py-1 rounded text-xs">#{index + 1}</span>
                  <span>{new Date(module.created_at).toLocaleDateString()}</span>
                  {module.audio_filename && (
                    <span className="flex items-center text-blue-600">
                      <Volume2 className="w-4 h-4 mr-1" />Audio
                    </span>
                  )}
                  {module.download_filename && (
                    <span className="flex items-center text-green-600">
                      <Upload className="w-4 h-4 mr-1" />Download
                    </span>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                <Button size="sm" variant="outline" onClick={() => setEditingModule(module)}
                  className="text-sage-600 border-sage-200 hover:bg-sage-50">
                  <Edit2 className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDeleteModule(module.id)}
                  className="text-red-600 border-red-200 hover:bg-red-50">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
            
            <div className="text-sm text-charcoal-600 bg-cream-50 p-3 rounded-lg max-h-32 overflow-y-auto ml-12">
              {module.text_content.substring(0, 200)}...
            </div>
          </div>
        ))}
      </div>

      {showCreateModal && (
        <CreateModuleModal 
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); fetchModules(); }}
        />
      )}

      {editingModule && (
        <EditModuleModal 
          module={editingModule}
          onClose={() => setEditingModule(null)}
          onSuccess={() => { setEditingModule(null); fetchModules(); }}
        />
      )}
    </div>
  );
};
